import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerOrden, crearOrden, actualizarOrden, siguienteFolio,
} from "../services/ordenesServicioService";

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  FechaEntrada:   today(),
  FechaEntrega:   today(),
  Cliente:        "",
  NombreCliente:  "",
  Contacto:       "",
  Trabajo:        "",
  PrecioEstimado: "",
  Iva:            "",
  Total:          "",
};

export default function OrdenesServicioFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toasts, closeToast, success: ok, error: err } = useToast();

  const [form, setForm] = useState(EMPTY);
  const [folio, setFolio] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [readonly, setReadonly] = useState(false);

  useEffect(() => {
    if (isEdit) {
      obtenerOrden(id)
        .then(o => {
          setFolio(o.ordenTrabajoId);
          setReadonly(o.status === "C");
          setForm({
            FechaEntrada:   o.fechaEntrada?.slice(0, 10) ?? today(),
            FechaEntrega:   o.fechaEntrega?.slice(0, 10) ?? today(),
            Cliente:        o.cliente,
            NombreCliente:  o.nombreCliente,
            Contacto:       o.contacto,
            Trabajo:        o.trabajo,
            PrecioEstimado: o.precioEstimado ?? "",
            Iva:            o.iva ?? "",
            Total:          o.total ?? "",
          });
        })
        .catch(() => err("No se pudo cargar la orden"))
        .finally(() => setLoading(false));
    } else {
      siguienteFolio().then(setFolio).catch(() => {});
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const calcTotales = (precio, ivaPorc) => {
    const p = parseFloat(precio) || 0;
    const ivaMonto = Math.round(p * (parseFloat(ivaPorc) / 100) * 100) / 100;
    return { iva: ivaMonto, total: Math.round((p + ivaMonto) * 100) / 100 };
  };

  const set = (field, val) => setForm(f => {
    const next = { ...f, [field]: val };
    if (field === "PrecioEstimado" || field === "_IvaPorc") {
      const t = calcTotales(
        field === "PrecioEstimado" ? val : next.PrecioEstimado,
        field === "_IvaPorc"       ? val : (next._IvaPorc ?? 16),
      );
      next.Iva   = t.iva   === 0 ? "" : String(t.iva);
      next.Total = t.total === 0 ? "" : String(t.total);
    }
    return next;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readonly) return;
    setSaving(true);
    try {
      const dto = {
        FechaEntrada:   form.FechaEntrada,
        FechaEntrega:   form.FechaEntrega,
        Cliente:        form.Cliente,
        NombreCliente:  form.NombreCliente,
        Contacto:       form.Contacto,
        Trabajo:        form.Trabajo,
        PrecioEstimado: form.PrecioEstimado !== "" ? Number(form.PrecioEstimado) : null,
        Iva:            form.Iva            !== "" ? Number(form.Iva)            : null,
        Total:          form.Total          !== "" ? Number(form.Total)          : null,
      };
      if (isEdit) {
        await actualizarOrden(id, dto);
        ok("Orden actualizada");
      } else {
        const res = await crearOrden(dto);
        ok("Orden creada");
        navigate(`/servicios/ordenes/${res.id}/editar`, { replace: true });
        return;
      }
    } catch (e) {
      err(e?.response?.data || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 32, color: "var(--clr-text-muted)" }}>Cargando…</div>;

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader
        title={isEdit ? `Orden #${folio}` : "Nueva Orden de Servicio"}
        subtitle={readonly ? "Solo lectura — orden cancelada" : "Datos generales"}
      />

      <form onSubmit={handleSubmit} style={{ maxWidth: 700 }}>
        {/* Fechas */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", marginBottom: 16 }}>
          <Field label="Fecha de Entrada *">
            <input className="lmd-input" type="date" value={form.FechaEntrada} required disabled={readonly}
              onChange={e => set("FechaEntrada", e.target.value)} />
          </Field>
          <Field label="Fecha de Entrega *">
            <input className="lmd-input" type="date" value={form.FechaEntrega} required disabled={readonly}
              onChange={e => set("FechaEntrega", e.target.value)} />
          </Field>
        </div>

        {/* Cliente */}
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "0 20px", marginBottom: 16 }}>
          <Field label="Clave cliente">
            <input className="lmd-input" value={form.Cliente} maxLength={15} disabled={readonly}
              placeholder="CLI001"
              onChange={e => set("Cliente", e.target.value)} />
          </Field>
          <Field label="Nombre del cliente *">
            <input className="lmd-input" value={form.NombreCliente} required maxLength={50} disabled={readonly}
              placeholder="Empresa o persona"
              onChange={e => set("NombreCliente", e.target.value)} />
          </Field>
        </div>

        <Field label="Contacto" style={{ marginBottom: 16 }}>
          <input className="lmd-input" value={form.Contacto} maxLength={50} disabled={readonly}
            placeholder="Nombre del contacto"
            onChange={e => set("Contacto", e.target.value)} />
        </Field>

        <Field label="Descripción del trabajo *" style={{ marginBottom: 16 }}>
          <textarea className="lmd-input" value={form.Trabajo} required maxLength={200} disabled={readonly}
            rows={4} placeholder="Detalle del servicio o reparación a realizar"
            style={{ resize: "vertical" }}
            onChange={e => set("Trabajo", e.target.value)} />
        </Field>

        {/* Precios */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px", marginBottom: 24 }}>
          <Field label="Precio estimado">
            <input className="lmd-input" type="number" step="0.01" min="0" value={form.PrecioEstimado}
              disabled={readonly} placeholder="0.00"
              onChange={e => set("PrecioEstimado", e.target.value)} />
          </Field>
          <Field label="IVA">
            <input className="lmd-input" type="number" step="0.01" min="0" value={form.Iva}
              disabled={readonly} placeholder="0.00"
              onChange={e => set("Iva", e.target.value)} />
          </Field>
          <Field label="Total">
            <input className="lmd-input" type="number" step="0.01" min="0" value={form.Total}
              disabled={readonly} placeholder="0.00"
              onChange={e => set("Total", e.target.value)} />
          </Field>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="lmd-btn lmd-btn-outline"
            onClick={() => navigate("/servicios/ordenes")}>
            ← Regresar
          </button>
          {!readonly && (
            <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear orden"}
            </button>
          )}
          {isEdit && (
            <button type="button" className="lmd-btn lmd-btn-outline"
              onClick={() => navigate(`/servicios/ordenes/${id}/print`)}>
              Imprimir
            </button>
          )}
          {isEdit && !readonly && (
            <button type="button" className="lmd-btn lmd-btn-outline"
              style={{ marginLeft: "auto" }}
              onClick={() => navigate(`/servicios/ordenes/${id}/firma`)}>
              ✍️ Ir a firma del cliente
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 4, ...style }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
